import { useState, useEffect, useRef } from 'react';
import { AppBar, Toolbar, Stack, Box, Typography } from '@mui/material';

import { useNavigation } from '../../hooks/useNavigation';
import NavigationMenu from './NavigationMenu';

import './menu.css';

interface TopNavigationBarProps {
    navRef?: React.Ref<HTMLDivElement>;
    overFooter: boolean;
    setOverFooter: (value: boolean) => void;
}

export default function TopNavigationBar({ navRef, overFooter, setOverFooter }: TopNavigationBarProps) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [menuAnimTrigger, setMenuAnimTrigger] = useState(false);
    const [animation, setAnimation] = useState<'in' | 'out'>('in');
    const [fadeIn, setFadeIn] = useState(false);
    const { goLanding, } = useNavigation();

    const prevOverFooterRef = useRef(overFooter);

    const handleMenuStates = () => {
        if (isMenuOpen) {
            setAnimation('out');
            setMenuAnimTrigger(false);
            setTimeout(() => {
                setIsMenuOpen(false);
                setOverFooter(prevOverFooterRef.current);
            }, 400);
        } else {
            prevOverFooterRef.current = overFooter;
            setOverFooter(false);
            setAnimation('in');
            setMenuAnimTrigger(true);
            setIsMenuOpen(true);
        }
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            setFadeIn(true);
        }, 10); 
        
        return () => clearTimeout(timeout);
    }, []);

    return (
        <>
            <AppBar
                position="fixed"
                id="top-nav"
                sx={{
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    width: '100%',
                    padding: '25px 0 0',
                    top: 0,
                    opacity: 1,
                    zIndex: 1300,
                    pointerEvents: 'none'
                }}
            >
                <Toolbar 
                    className={`fade-up ${fadeIn ? 'animate' : ''}`}
                    sx={{ 
                        width: '100%', 
                        display: 'flex', 
                        justifyContent: 'center', 
                        p: 0, 
                        pointerEvents: 'none', 
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                            maxWidth: '1240px',
                            width: '100%',
                            height: '60px',
                            px: 3,
                            marginBottom: '6px',
                            pointerEvents: 'none', 
                        }}
                    >
                        <Box
                                            ref={navRef}
                            onClick={() => goLanding()}
                            role="button"
                            aria-label="Go to homepage"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    goLanding();
                                }
                            }}
                            display="flex"
                            alignItems="center"
                            tabIndex={1}
                            sx={{
                                position: 'relative',
                                fontWeight: 700,
                                letterSpacing: '0.04em',
                                cursor: 'pointer',
                                textAlign: 'center',
                                height: 'fit-content',
                                overflow: 'hidden',
                                pointerEvents: 'auto',
                            }}
                            className="tab-indictor"
                        >
                            <Typography 
                                className="menu-text menu-text-style"
                                sx={{color: overFooter ? "black" : "white"}}
                            >
                                Broadcast Studio
                            </Typography>
                        </Box>

                        <Box 
                            display="flex" 
                            alignItems="center" 
                            justifyContent={"center"}
                            gap={2} 
                            sx={{
                                marginLeft: 'auto', 
                                marginRight: '20px' 
                            }}
                        >
                            <div
                                className={`menu-bar tab-indictor ${menuAnimTrigger ? 'open' : ''}`}
                                tabIndex={2}
                                aria-label="Menu icon"
                                role="button"
                                style={{ marginRight: '10px', pointerEvents: 'auto', }}
                                onClick={() => handleMenuStates()}
                                onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleMenuStates();
                                    }
                                }}
                            >
                                <div className={`bar bar-top ${overFooter ? 'bar-dark' : 'bar-light'}`} />
                                <div className={`bar bar-middle ${overFooter ? 'bar-dark' : 'bar-light'}`} />
                                <div className={`bar bar-bottom ${overFooter ? 'bar-dark' : 'bar-light'}`} />
                            </div>

                            <a
                                href="https://6hhrjuleqp4.typeform.com/to/NoPNTJFG"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`record-btn ${overFooter ? 'bar-dark' : 'bar-light'}`}
                                aria-label="Open recording form in new tab"
                                tabIndex={3}
                                style={{pointerEvents: 'auto',}}
                            >
                                <Typography className={`menu-bold-text${overFooter ? 'bar-dark' : 'bar-light'}`}>Record</Typography>
                            </a>
                        </Box>
                    </Stack>
                </Toolbar>
            </AppBar>
            
            {isMenuOpen && (
                <NavigationMenu setIsMenuOpen={setIsMenuOpen} setMenuAnimTrigger={setMenuAnimTrigger} animation={animation} />
            )}
        </>
    );
}
